import axios from "axios";
import i18n from "../../../i18n";

import { getClaims } from "../../../modules/login";
import { DailyListKeys } from "./Constants";
import { DateFormat, HttpCodes } from "../../../modules/constants";
import { DEFAULT_MOMENT_LOCALE } from "../../../i18n/helpers";
import { handleHttpErrors } from "../../../modules/http";

//Constants
const FETCHING_ENTRIES = "FETCHING_ENTRIES";
const FETCHING_ENTRIES_ERROR = "FETCHING_ENTRIES_ERROR";
const SET_DAY_ENTRIES = "SET_DAY_ENTRIES";
const SET_YESTERDAY_ENTRIES = "SET_YESTERDAY_ENTRIES";
const COMPLETING_DAY = "COMPLETING_DAY";
const SET_DAY_COMPLETED = "SET_DAY_COMPLETED";
const LOGOUT = "LOGOUT";
const SET_DAILY_TARGETS = "SET_DAILY_TARGETS";
const RESET_STATE = "RESET_STATE";

//Acessors
const getYesterdayEntries = (state) => state?.yesterdayEntries;
const getTodayEntries = (state) => state?.todayEntries;
const getIsLoadingEntries = (state) => state?.loadingEntries;
const getLoadingEntriesError = (state) => state?.loadingEntriesError;
const getIsCompletingDay = (state) => state?.completingDay;
const getDayCompleted = (state) => state?.dayCompleted;
const getStudies = (state) => state?.loginReducer?.studies;
const getDailyTargets = (state) => state?.dailyTargets;

const canCompleteDay = (state) =>
  getClaims(state, "mealLog.completeDay.canComplete");
const canViewMacronutrients = (state) =>
  getClaims(state, "mealLog.macronutrients.canView");
const canAddMeal = (state) => getClaims(state, "mealLog.canCreate");
const canEditMeal = (state) => getClaims(state, "mealLog.canEdit");
const canDeleteMeal = (state) => getClaims(state, "mealLog.canSoftDelete");

//Default state
const initialState = {
  yesterdayEntries: null,
  todayEntries: null,
  loadingEntries: false,
  loadingEntriesError: false,
  completingDay: false,
  dayCompleted: false,
  studies: null,
  dailyTargets: null,
};

export const dailyEntries = (state = initialState, action) => {
  switch (action.type) {
    case FETCHING_ENTRIES: {
      return Object.assign({}, state, {
        loadingEntries: true,
      });
    }
    case FETCHING_ENTRIES_ERROR: {
      return Object.assign({}, state, {
        loadingEntries: false,
        loadingEntriesError: true,
      });
    }
    case SET_YESTERDAY_ENTRIES: {
      const { entries } = action;
      return Object.assign({}, state, {
        yesterdayEntries: entries,
        loadingEntries: false,
        loadingEntriesError: false,
      });
    }
    case SET_DAY_ENTRIES: {
      const { entries } = action;
      return Object.assign({}, state, {
        todayEntries: entries,
        loadingEntries: false,
        loadingEntriesError: false,
      });
    }
    case COMPLETING_DAY: {
      return Object.assign({}, state, {
        completingDay: true,
      });
    }
    case SET_DAY_COMPLETED: {
      return Object.assign({}, state, {
        dayCompleted: true,
        completingDay: false,
      });
    }
    case SET_DAILY_TARGETS: {
      const { dailyTargets } = action;
      return Object.assign({}, state, {
        dailyTargets: dailyTargets,
      });
    }
    case LOGOUT:
      return initialState;
    case RESET_STATE:
      return initialState;
    default:
      return state;
  }
};

//Mutators
const fetchEntries = (date) => (dispatch) => {
  const params = {
    StartDate: date.date
      ?.locale(DEFAULT_MOMENT_LOCALE)
      .format("YYYY/MM/DDT00:00:00"),
    EndDate: date.date
      ?.locale(DEFAULT_MOMENT_LOCALE)
      .format("YYYY/MM/DDT23:59:59"),
  };
  dispatch({ type: FETCHING_ENTRIES });
  return axios
    .get(`/api/diet/dailyentries/`, { params })
    .then(({ data }) => {
      dispatch({ type: SET_DAILY_TARGETS, dailyTargets: data?.dailyTargets });

      if (date.key === DailyListKeys.todayListName) {
        dispatch({
          type: SET_DAY_ENTRIES,
          entries: formatDailyEntries(data.dailyEntries, date.date),
        });
      } else if (date.key === DailyListKeys.yesterdayListName) {
        dispatch({
          type: SET_YESTERDAY_ENTRIES,
          entries: formatDailyEntries(data.dailyEntries, date.date),
        });
      }
    })
    .catch(() => {
      dispatch({ type: FETCHING_ENTRIES_ERROR });
    });
};

const completeDay = (formData) => async (dispatch) => {
  dispatch({ type: COMPLETING_DAY, completingDay: true });
  return axios
    .post(`/api/diet/dailyentry/complete`, formData)
    .then(() => {
      dispatch({ type: SET_DAY_COMPLETED, dayCompleted: true });
      return Promise.resolve(i18n.t("appText.dietDiary.completeDay.success"));
    })
    .catch((error) => {
      return handleHttpErrors(error.request?.status, {
        default: i18n.t("errorText.dietDiary.completeDay"),
        conflict: i18n.t("errorText.dietDiary.completeDay.409"),
      });
    });
};

const deleteEntryMeal = (mealId) => (dispatch) => {
  const params = {
    mealID: mealId,
  };

  return axios
    .delete(`/api/diet/meals`, { params })
    .then(() => {
      return Promise.resolve(i18n.t("appText.dietDiary.entry.remove.success"));
    })
    .catch((error) => {
      return handleHttpErrors(error.request?.status, {
        default: i18n.t("errorText.dietDiary.entry.delete"),
      });
    });
};

const resetState = () => (dispatch) => {
  dispatch({ type: RESET_STATE });
};

const formatDailyEntries = (dailyEntries, date) => {
  const dailyEntry =
    dailyEntries.length > 0
      ? dailyEntries[0]
      : {
          date: date.format(DateFormat.START_DATE),
          completed: false,
          fastingDay: false,
          meals: [],
          sickDay: false,
          sickDayDate: null,
          missedSchool: false,
          didntFeelWell: false,
          studyVisitOrMedicalApt: false,
        };

  dailyEntry.formattedMeals = [];

  dailyEntry.meals.forEach((meal) => {
    const { mealJson, mealDate } = meal;
    const mealsItems = [];
    let jsonData = mealJson;
    jsonData = typeof jsonData === "string" ? JSON.parse(mealJson) : jsonData;

    let rowTitle = "";
    let rowDate = null;
    if (jsonData) {
      jsonData.items.forEach((jsonElem) => {
        rowTitle += `${jsonElem.title}, `;
        rowDate = mealDate;
        mealsItems.push({
          title: jsonElem.title,
          date: mealDate,
          dayDate: date,
          elem: meal,
        });
      });
    }

    // removing last comma
    rowTitle = rowTitle.slice(0, -2);

    dailyEntry.formattedMeals.push({
      ...meal,
      title: rowTitle,
      json: mealJson,
      date: rowDate,
      items: mealsItems,
    });
  });

  dailyEntry.formattedMeals.sort((elemPrev, elemNext) =>
    elemNext.date.localeCompare(elemPrev.date)
  );

  return dailyEntry;
};

export const mapStateToProps = (state) => {
  return {
    yesterdayEntries: getYesterdayEntries(state),
    todayEntries: getTodayEntries(state),
    loadingEntries: getIsLoadingEntries(state),
    loadingEntriesError: getLoadingEntriesError(state),
    completingDay: getIsCompletingDay(state),
    dayCompleted: getDayCompleted(state),
    studies: getStudies(state),
    dailyTargets: getDailyTargets(state),
    canCompleteDay: canCompleteDay(state),
    canViewMacronutrients: canViewMacronutrients(state),
    canAddMeal: canAddMeal(state),
    canEditMeal: canEditMeal(state),
    canDeleteMeal: canDeleteMeal(state),
  };
};

export const mapDispatchToProps = {
  fetchEntries,
  completeDay,
  resetState,
  deleteEntryMeal,
};
